import { useTranslation } from 'react-i18next';

import {
  differenceInMinutes,
  endOfDay,
  isSameDay,
  isSameMinute,
  isSameYear,
  startOfDay,
  subMinutes,
} from 'date-fns';

import { OjEvent, StackEvent } from '../../../shared/api';
import { useLocalisedDateFormat } from '../../../shared/lib/useLocalisedDateFormat';
import { MINUTES_IN_DAY } from '../model';
import { fixFullDayEventStart } from './fixFullDayEventStart';
import { getEventInterval } from './getEventInterval';
import { isFullDayInterval } from './isFullDayInterval';
import { isStackEvent } from './isStackEvent';

type FormatOptions = {
  /**
   * The hero's form: short weekday ("Mon, 28 Sep") and an en dash between the
   * times ("12:03 – 17:03"), so the line fits the hero's 300px on one line in
   * every locale. Everywhere else keeps the long weekday and the hyphen.
   */
  compact?: boolean;
};

export function useFormatEventDateAndTime() {
  const { t } = useTranslation();
  const { format } = useLocalisedDateFormat();

  return {
    formatEventDateAndTime: (
      event: OjEvent | StackEvent,
      hideFirstDayDate = false,
      { compact = false }: FormatOptions = {},
    ) => {
      const dayFormat = compact ? 'EEE, dd MMM' : 'EEEE, dd MMM';
      const dayFormatWithYear = compact
        ? 'EEE, dd MMM yyy'
        : 'EEEE, dd MMM yyy';
      const dash = compact ? '–' : '-';

      const { end, start } = getEventInterval(
        isStackEvent(event) ? event : fixFullDayEventStart(event),
      );

      const isFullDay = isFullDayInterval({ end, start });
      const isStartSameYear = isSameYear(start, new Date());

      if (isSameDay(start, end) || isFullDay) {
        const formatedEventTime = `${format(start, 'HH:mm')} ${dash} ${format(end, 'HH:mm')}`;

        const dateSrt = `${format(start, isStartSameYear ? dayFormat : dayFormatWithYear)} · `;

        return [
          hideFirstDayDate ? '' : dateSrt,
          isFullDay ? t('all:feed.event.full_day') : formatedEventTime,
        ].join('');
      }

      const startDateStr = hideFirstDayDate
        ? ''
        : format(start, isStartSameYear ? dayFormat : dayFormatWithYear);
      const startDateTime = isSameMinute(start, startOfDay(start))
        ? t('all:feed.event.full_day')
        : format(start, 'HH:mm');

      const endOfPeriod =
        differenceInMinutes(end, start) > MINUTES_IN_DAY &&
        isSameMinute(end, startOfDay(end))
          ? subMinutes(end, 1)
          : end;
      const isEndSameYear = isSameYear(endOfPeriod, new Date());

      const endDateStr = format(
        endOfPeriod,
        isEndSameYear ? dayFormat : dayFormatWithYear,
      );
      const endDateTime = isSameMinute(endOfPeriod, endOfDay(endOfPeriod))
        ? t('all:feed.event.full_day')
        : format(endOfPeriod, 'HH:mm');

      return `${[startDateStr, startDateTime].filter(Boolean).join(' · ')} ${dash} \n${[endDateStr, endDateTime].join(' · ')}`;
    },
  };
}
